import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { 
  Building2, Home, CreditCard, Wrench, CheckCircle, Clock, 
  DollarSign, Calendar, LogOut, ArrowRight, User, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

interface MaintenanceRequest {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
}

export default function TenantPortal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { currentUser, logout } = useStore();
  const [activeTab, setActiveTab] = useState<'overview' | 'payments' | 'maintenance'>('overview');
  const [payingRent, setPayingRent] = useState(false);

  // Mock Tenant Data
  const rentAmount = 1450;
  const dueDate = '2026-07-01';
  const propertyAddress = '809 West Ave Apt 4B, Austin, TX';
  const landlordName = 'Metro Property Management';

  const [requests, setRequests] = useState<MaintenanceRequest[]>([
    {
      id: 'req-1',
      title: 'Drywall Hole in Master Bedroom',
      description: 'Patch hole in master bedroom and paint wall to match existing color.',
      status: 'pending',
      priority: 'medium',
      createdAt: '2026-06-05'
    },
    {
      id: 'req-2',
      title: 'Leaking Faucet in Master Bath',
      description: 'The master bathroom sink faucet is constantly dripping.',
      status: 'completed',
      priority: 'low',
      createdAt: '2026-05-12'
    }
  ]);

  const [newReq, setNewReq] = useState({ title: '', description: '', priority: 'medium' as 'low' | 'medium' | 'high' });

  const handlePayRent = () => {
    setPayingRent(true);
    const load = toast.loading("Processing payment security...");
    setTimeout(() => {
      toast.dismiss(load);
      toast.success("Rent payment of $1,450.00 processed successfully!");
      setPayingRent(false);
    }, 2000);
  };

  const handleAddRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReq.title.trim() || !newReq.description.trim()) {
      toast.error("Please fill out all fields");
      return;
    }
    const req: MaintenanceRequest = {
      id: `req-${Date.now()}`,
      title: newReq.title,
      description: newReq.description,
      status: 'pending',
      priority: newReq.priority,
      createdAt: new Date().toISOString().split('T')[0]
    };
    setRequests(prev => [req, ...prev]);
    setNewReq({ title: '', description: '', priority: 'medium' });
    toast.success("Maintenance request submitted successfully!");
  };

  const handleSignOut = async () => {
    if (supabase) await supabase.auth.signOut();
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#07080d] text-white flex flex-col font-sans select-none animate-fade-in">
      {/* Background Glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#07080d]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
            <Home className="text-indigo-400 w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wider uppercase italic">Tenant Portal</h1>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-black">Powered by WholeScale OS</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col text-right">
            <span className="text-sm font-bold">{currentUser?.name || 'Tenant'}</span>
            <span className="text-[10px] text-indigo-400 uppercase font-black tracking-widest">Verified Resident</span>
          </div>
          <button 
            onClick={handleSignOut}
            className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8 flex flex-col md:flex-row gap-8 relative z-10">
        {/* Left Navigation Bar */}
        <aside className="w-full md:w-64 shrink-0 space-y-4">
          <div className="bg-zinc-900/40 border border-white/5 rounded-3xl p-6 space-y-6">
            <div>
              <p className="text-[10px] font-black uppercase text-zinc-500 tracking-wider mb-2">My Property</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-indigo-650 flex items-center justify-center font-black text-white italic">
                  <Building2 size={20} className="text-indigo-300" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold truncate">Apt 4B</p>
                  <p className="text-[10px] text-zinc-400 truncate">809 West Ave</p>
                </div>
              </div>
            </div>

            <div className="h-px bg-white/5" />

            <div className="space-y-1">
              {[
                { id: 'overview', label: 'Lease Overview', icon: Home },
                { id: 'payments', label: 'Payments', icon: CreditCard },
                { id: 'maintenance', label: 'Maintenance', icon: Wrench }
              ].map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-left transition-all ${
                      active
                        ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Right Content Panel */}
        <section className="flex-1 space-y-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold uppercase italic tracking-wider">Lease Overview</h2>
                <p className="text-xs text-zinc-500 mt-1">Details about your current rental agreement and landlord.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-zinc-900/30 border border-white/5 rounded-3xl space-y-4">
                  <h3 className="text-sm font-bold uppercase text-zinc-400 tracking-wider">Property Information</h3>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-white">{propertyAddress}</p>
                    <p className="text-xs text-zinc-400">Landlord: <span className="text-white font-bold">{landlordName}</span></p>
                  </div>
                </div>

                <div className="p-6 bg-zinc-900/30 border border-white/5 rounded-3xl space-y-4">
                  <h3 className="text-sm font-bold uppercase text-zinc-400 tracking-wider">Rent Summary</h3>
                  <div className="space-y-2">
                    <p className="text-2xl font-black text-indigo-400">${rentAmount.toLocaleString()} <span className="text-xs text-zinc-400 font-medium">/ month</span></p>
                    <p className="text-xs text-zinc-400">Next Due Date: <span className="text-white font-bold">{dueDate}</span></p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold uppercase italic tracking-wider">Rent & Billing</h2>
                <p className="text-xs text-zinc-500 mt-1">Make payments and view your billing statement.</p>
              </div>

              <div className="max-w-2xl bg-zinc-900/30 border border-white/5 rounded-3xl p-8 space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-bold">Outstanding Balance</h3>
                    <p className="text-xs text-zinc-500 mt-1">Due on {dueDate}</p>
                  </div>
                  <span className="text-3xl font-black text-indigo-400">${rentAmount.toLocaleString()}</span>
                </div>

                <button 
                  onClick={handlePayRent}
                  disabled={payingRent}
                  className="w-full py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-xs font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                >
                  <CreditCard size={16} /> Pay Rent Now
                </button>

                <div className="h-px bg-white/5" />

                <div>
                  <h4 className="text-xs font-black uppercase text-zinc-500 tracking-widest mb-4">Payment History</h4>
                  <div className="space-y-3">
                    {[
                      { date: '2026-06-01', desc: 'Rent Payment - June 2026', amount: -1450, status: 'success' },
                      { date: '2026-05-01', desc: 'Rent Payment - May 2026', amount: -1450, status: 'success' }
                    ].map((tx, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs p-3.5 bg-black/20 rounded-xl border border-white/5">
                        <div>
                          <p className="font-bold text-white">{tx.desc}</p>
                          <p className="text-[10px] text-zinc-500 mt-0.5">{tx.date}</p>
                        </div>
                        <span className="font-bold text-emerald-400">${Math.abs(tx.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold uppercase italic tracking-wider">Maintenance Requests</h2>
                <p className="text-xs text-zinc-500 mt-1">Report items requiring repair or maintenance.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Submit Form */}
                <form onSubmit={handleAddRequest} className="bg-zinc-900/30 border border-white/5 rounded-3xl p-6 space-y-4">
                  <h3 className="text-base font-bold uppercase tracking-wider text-indigo-400 italic">Submit Request</h3>
                  
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-widest block mb-1">Issue Title</label>
                    <input 
                      type="text" 
                      value={newReq.title}
                      onChange={(e) => setNewReq(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Toilet is leaking"
                      className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500" 
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-widest block mb-1">Priority</label>
                    <select 
                      value={newReq.priority}
                      onChange={(e) => setNewReq(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 appearance-none"
                    >
                      <option value="low">Low (Non-urgent)</option>
                      <option value="medium">Medium (Standard)</option>
                      <option value="high">High (Urgent)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-widest block mb-1">Description</label>
                    <textarea 
                      value={newReq.description}
                      onChange={(e) => setNewReq(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the issue in detail..."
                      className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 h-28 resize-none" 
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-xs font-bold uppercase tracking-widest transition-all"
                  >
                    Submit Request
                  </button>
                </form>

                {/* History List */}
                <div className="space-y-4">
                  <h3 className="text-base font-bold uppercase tracking-wider text-zinc-400 italic">Request History</h3>
                  <div className="space-y-3">
                    {requests.map(req => (
                      <div key={req.id} className="p-5 bg-zinc-900/30 border border-white/5 rounded-2xl flex flex-col gap-3">
                        <div className="flex justify-between items-center">
                          <h4 className="text-xs font-bold text-white leading-tight">{req.title}</h4>
                          <div className="flex gap-1.5">
                            {req.status === 'completed' ? (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px] font-black uppercase tracking-wider">
                                Completed
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[9px] font-black uppercase tracking-wider animate-pulse">
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-[11px] text-zinc-400 leading-relaxed">{req.description}</p>
                        <div className="flex items-center justify-between text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                          <span>Priority: <span className={req.priority === 'high' ? 'text-red-400' : req.priority === 'medium' ? 'text-amber-400' : 'text-blue-400'}>{req.priority}</span></span>
                          <span>Submitted: {req.createdAt}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
